<template lang="pug">
// We use this to catch some common bugs in a way that's easier to debug.
// This catches rules that are old Vue2 Vee-Validate strings.
// It also catches when a single function has been passed in as :rules, rather than an array of functions
//    This is a very common error and can be hard to debug.
p.text-red.font-weight-bold(v-if='isDebugMode && !Array.isArray(rules)') FOR PROGRAMMER: rules isn't an array!: {{ rules }}

v-text-field(
    :rules='rulesAugmented'
    v-bind='$attrs'
    v-model='innerValue'
    :success='valid'
    :single-line='singleLine'
    :clearable='clearable'
    :hide-details='hideDetails'
    :validate-on='validateOn'
    :persistent-placeholder='persistentPlaceholder'
    :prepend-icon='prependIcon'
    :density='density'
    :readonly='readonly'
    :elevation='Number(elevation)'
    :hint='hint'
    :disabled='disabled'
)
    template(v-slot:append-inner)
        slot(name='append')
</template>

<script lang="ts">
import { PropType } from 'vue';
import type { TypeValidationRules } from '@/common/Validation/ValidationRules';
import { rules } from '@/common/';

export default {
    inheritAttrs: false,
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            // Special Prop., used by v-model
            type: [String, Number],
            default: '',
        },
        rules: {
            type: Array as PropType<TypeValidationRules>,
            default: () => [],
        },
        singleLine: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: Boolean,
            default: false,
        },
        validateOn: {
            // See https://vuetifyjs.com/en/components/forms/#validation-state for options:
            type: String,
            default: 'blur',
        },
        persistentPlaceholder: {
            type: Boolean,
            default: false,
        },
        allowSpecialCharacters: {
            type: Boolean,
            default: false,
        },
        prependIcon: {
            type: String,
            default: '',
        },
        density: {
            type: String,
            default: 'default',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        elevation: {
            type: [Number, String],
            default: 0,
        },
        hint: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        innerValue: '' as string | number,
        isDebugMode: import.meta.env.DEV,
        valid: true,
    }),
    computed: {
        rulesAugmented(): TypeValidationRules {
            if (this.allowSpecialCharacters) {
                return this.rules;
            }
            return [...this.rules, rules.noSpecialCharacters];
        },
    },
    watch: {
        // Handles internal model changes.
        innerValue(newVal) {
            this.$emit('update:modelValue', newVal);
        },
        // Handles external model changes.
        modelValue(newVal) {
            this.innerValue = newVal;
        },
        rules: {
            handler(newValue) {
                if (!Array.isArray(newValue)) {
                    console.error(
                        `ValidatedTextField.vue::rules::handler: Expected an array of rule functions but newValue is: `,
                        newValue,
                    );
                }
            },
            immediate: true,
            deep: true,
        },
    },
    created() {
        if (this.modelValue) {
            this.innerValue = this.modelValue;
        }
    },
};
</script>
<style scoped scss>
@import '@/common/validatedInputStyles.scss';
</style>
